<template>
  <header 
    class="sticky top-0 z-50 border-b transition-all duration-300 ease-in-out"
    :class="scrolled 
      ? 'border-ink-200/50 bg-white/90 backdrop-blur-md shadow-sm py-2' 
      : 'border-transparent bg-white/50 backdrop-blur-sm py-3 md:py-4'"
    aria-label="Site header">
    
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
      
      <router-link :to="`/${langue}`"
        class="group flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 rounded-xl transition-transform duration-300 hover:scale-105 origin-left">
        <img src="@/assets/img/logo/ar.svg" alt="Anjah R." class="h-10 md:h-12 w-auto transition-all" />
        <span class="sr-only">Accueil</span>
      </router-link>

      <nav class="hidden md:flex items-center gap-2 p-1 rounded-full bg-ink-50/50 border border-ink-100/50 backdrop-blur-md" aria-label="Primary">
        <template v-for="(nav, i) in navLinks" :key="i">
          
          <a v-if="isExternal(nav.action)" :href="nav.action" target="_blank" rel="noopener"
            class="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-ink-600 hover:text-brand-600 hover:bg-white hover:shadow-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300">
            <span>{{ nav.label || 'Lien' }}</span>
            <i class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-70"></i>
          </a>

          <router-link v-else :to="localized(nav.action)"
            class="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
            :class="isActive(localized(nav.action)) 
              ? 'bg-brand-600 text-white shadow-md shadow-brand-500/20' 
              : 'text-ink-600 hover:text-brand-700 hover:bg-white hover:shadow-sm'">
            {{ nav.label || 'Lien' }}
          </router-link>
        </template>
      </nav>

      <div class="relative hidden md:block">
        <button @click="toggleLangDropdown"
          class="flex items-center gap-2 px-3 py-2 rounded-full border border-ink-200/60 bg-white/80 hover:bg-white text-ink-700 hover:text-brand-600 hover:shadow-md hover:border-brand-100 transition-all duration-300"
          aria-haspopup="listbox" :aria-expanded="isLangDropdownOpen ? 'true' : 'false'"
          :aria-label="langue === 'fr' ? 'Langue : Français' : 'Language: English'">
          <span :class="`fi ${langue === 'fr' ? 'fi-ca' : 'fi-us'} w-5 h-5 rounded-full object-cover shadow-sm border border-white`"></span>
          <span class="text-sm font-bold tracking-wide">{{ langue === 'fr' ? 'FR' : 'EN' }}</span>
          <i class="fas fa-chevron-down text-[10px] opacity-60 transition-transform duration-300" :class="isLangDropdownOpen ? 'rotate-180' : ''"></i>
        </button>

        <transition name="fade-scale">
          <ul v-if="isLangDropdownOpen"
            class="absolute right-0 mt-3 w-40 bg-white/95 backdrop-blur-xl border border-ink-100 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] overflow-hidden p-1 z-50"
            role="listbox">
            <li>
              <button class="w-full text-left px-3 py-2 rounded-xl hover:bg-brand-50 text-ink-700 hover:text-brand-700 flex items-center gap-3 transition-colors"
                @click="changeLanguage('fr')">
                <span class="fi fi-ca w-5 h-5 rounded-full shadow-sm"></span>
                <span class="font-medium text-sm">Français</span>
                <i v-if="langue === 'fr'" class="fas fa-check text-xs text-brand-600 ml-auto"></i>
              </button>
            </li>
            <li>
              <button class="w-full text-left px-3 py-2 rounded-xl hover:bg-brand-50 text-ink-700 hover:text-brand-700 flex items-center gap-3 transition-colors"
                @click="changeLanguage('en')">
                <span class="fi fi-us w-5 h-5 rounded-full shadow-sm"></span>
                <span class="font-medium text-sm">English</span>
                <i v-if="langue === 'en'" class="fas fa-check text-xs text-brand-600 ml-auto"></i>
              </button>
            </li>
          </ul>
        </transition>
      </div>

      <button
        class="md:hidden relative group p-2 rounded-xl text-ink-800 hover:bg-brand-50 hover:text-brand-600 transition-colors focus:outline-none"
        @click="isMobileMenuOpen = !isMobileMenuOpen" :aria-expanded="isMobileMenuOpen ? 'true' : 'false'"
        aria-controls="mobile-menu" aria-label="Menu">
        <div class="flex flex-col gap-[5px] w-6">
            <span class="h-0.5 w-full bg-current rounded-full transition-transform duration-300" :class="isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''"></span>
            <span class="h-0.5 w-full bg-current rounded-full transition-opacity duration-300" :class="isMobileMenuOpen ? 'opacity-0' : ''"></span>
            <span class="h-0.5 w-full bg-current rounded-full transition-transform duration-300" :class="isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''"></span>
        </div>
      </button>
    </div>

    <transition name="slide">
      <div v-if="isMobileMenuOpen" id="mobile-menu" class="md:hidden absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b border-ink-100 shadow-xl"
        @keydown.escape="isMobileMenuOpen = false">
        <nav class="px-4 py-6 flex flex-col gap-2" aria-label="Mobile">
          <template v-for="(nav, i) in navLinks" :key="'m' + i">
            <a v-if="isExternal(nav.action)" :href="nav.action" target="_blank" rel="noopener"
              @click="isMobileMenuOpen = false"
              class="group flex items-center justify-between px-4 py-3 rounded-xl text-ink-600 hover:bg-brand-50 hover:text-brand-700 transition-all duration-200">
              <span class="font-semibold text-lg">{{ nav.label || 'Lien' }}</span>
              <i class="fa-solid fa-arrow-up-right-from-square text-xs opacity-50 group-hover:translate-x-1 transition-transform"></i>
            </a>

            <router-link v-else :to="localized(nav.action)" @click="isMobileMenuOpen = false"
              class="block px-4 py-3 rounded-xl font-semibold text-lg transition-all duration-200"
              :class="isActive(localized(nav.action)) 
                ? 'bg-brand-50 text-brand-700 border border-brand-100' 
                : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'">
              {{ nav.label || 'Lien' }}
            </router-link>
          </template>

          <div class="pt-6 mt-4 border-t border-ink-100/50">
            <label class="block mb-3 text-xs font-bold uppercase tracking-wider text-ink-400 pl-1">
              {{ langue === 'fr' ? 'Langue' : 'Language' }}
            </label>
            <div class="grid grid-cols-2 gap-3">
              <button
                class="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-ink-200 transition-all active:scale-95"
                :class="langue === 'fr' ? 'bg-brand-600 text-white border-brand-600 shadow-md' : 'bg-white hover:bg-ink-50'"
                @click="changeLanguage('fr')">
                <span class="fi fi-ca w-5 h-5 rounded-full shadow-sm border border-white/20"></span>
                <span class="font-medium">Français</span>
              </button>
              <button
                class="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-ink-200 transition-all active:scale-95"
                :class="langue === 'en' ? 'bg-brand-600 text-white border-brand-600 shadow-md' : 'bg-white hover:bg-ink-50'"
                @click="changeLanguage('en')">
                <span class="fi fi-us w-5 h-5 rounded-full shadow-sm border border-white/20"></span>
                <span class="font-medium">English</span>
              </button>
            </div>
          </div>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script>
import { mapGetters } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

export default {
  name: "HighHeader",
  data() {
    return {
      navLinks: [],
      isMobileMenuOpen: false,
      isLangDropdownOpen: false,
      scrolled: false,
      _unwatchRoute: null,
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    langue: {
      get() {
        return this.getLangue;
      },
      set(v) {
        this.$store.commit("setLangue", v);
      },
    },
  },
  methods: {
    /**
     * RÉALISME TECHNIQUE : 
     * L'utilisation de apiUrl() ici créait le doublon.
     * En passant directement "GlobalContent" à l'instance http, 
     * Axios utilise sa baseURL (/api) et ajoute les paramètres proprement.
     */
    async fetchPages(lang) {
      const l = String(lang || this.$route.params.langue || this.langue || "fr").trim();

      if (!l || l === "undefined" || l === "null") return;

      try {
        const { data } = await http.get("GlobalContent", {
          params: {
            type: "navHeader",
            langue: l
          }
        });
        this.navLinks = Array.isArray(data?.boutons) ? data.boutons : [];
      } catch (e) {
        if (e.response && e.response.status === 400) return;
        console.error("[HighHeader] Erreur chargement nav:", e);
      }
    },

    // ---------------- Utils liens ----------------
    isExternal(path) {
      return /^https?:\/\//i.test(String(path || ""));
    },

    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },

    localized(path) {
      return this.ensureLocalePrefix(path, this.langue);
    },

    setLanguage(lang) {
      this.$store.commit("setLangue", lang);
      localStorage.setItem("locale", lang);
    },

    // ---------------- Résolution de Slugs (Multi-langue) ----------------
    parseServiceDetailPath(path) {
      const m = /^\/(fr|en)\/services\/([^\/?#]+)/i.exec(String(path || ""));
      return m ? { lang: m[1], slug: decodeURIComponent(m[2]) } : null;
    },

    parseServiceTagPath(path) {
      const m = /^\/(fr|en)\/services\/tag\/([^\/?#]+)/i.exec(String(path || ""));
      return m ? { lang: m[1], tag: decodeURIComponent(m[2]) } : null;
    },

    async resolveDetailSlugForLang(targetLang) {
      const info = this.parseServiceDetailPath(this.$route.path);
      if (!info) return null;
      try {
        const srcId = await fetchPageIdByCode("home", info.lang);
        const dstId = await fetchPageIdByCode("home", targetLang);

        const { data } = await http.get("service-detail/resolve-slug", {
          params: {
            source_page_id: srcId,
            target_page_id: dstId,
            slug: info.slug
          },
        });
        return data?.found && data?.slug ? String(data.slug) : null;
      } catch (e) {
        console.error("[HighHeader] Erreur resolve-slug:", e);
        return null;
      }
    },

    async resolveTagForLang(targetLang) {
      const info = this.parseServiceTagPath(this.$route.path);
      if (!info) return null;
      try {
        const srcId = await fetchPageIdByCode("home", info.lang);
        const dstId = await fetchPageIdByCode("home", targetLang);

        const { data } = await http.get("services/resolve-tag", {
          params: {
            source_page_id: srcId,
            target_page_id: dstId,
            tag: info.tag
          },
        });
        return data?.found && data?.tag ? String(data.tag) : info.tag;
      } catch {
        return info.tag;
      }
    },

    mapRouteNameForLang(curName, targetLang) {
      if (targetLang === "fr" && curName === "about-en") return "about-fr";
      if (targetLang === "en" && curName === "about-fr") return "about-en";
      return curName || undefined;
    },

    async changeLanguage(lang) {
      this.isLangDropdownOpen = false;
      this.setLanguage(lang);

      const { name, params, query, hash, path } = this.$route;
      const safeQuery = { ...query };

      // 1) Cas Service Detail
      const isDetail = this.parseServiceDetailPath(path);
      if (isDetail && name === "service-detail") {
        const resolved = await this.resolveDetailSlugForLang(lang);
        this.$router.push({
          name: "service-detail",
          params: { ...params, langue: lang, slug: resolved || isDetail.slug },
          query: safeQuery,
          hash,
        }).catch(() => { });
        this.fetchPages(lang);
        return;
      }

      // 2) Cas Liste par Tag
      const isTag = this.parseServiceTagPath(path);
      if (isTag && name === "services-by-tag") {
        const targetTag = await this.resolveTagForLang(lang);
        this.$router.push({
          name: "services-by-tag",
          params: { ...params, langue: lang, tag: targetTag },
          query: safeQuery,
          hash,
        }).catch(() => { });
        this.fetchPages(lang);
        return;
      }

      // 3) Autres routes
      const targetName = this.mapRouteNameForLang(name, lang);
      if (targetName) {
        this.$router.push({
          name: targetName,
          params: { ...params, langue: lang },
          query: safeQuery,
          hash,
        }).catch(() => { });
      } else {
        const full = this.$route.fullPath || "/";
        const next = /^\/(fr|en)\b/.test(full) ? full.replace(/^\/(fr|en)/, `/${lang}`) : `/${lang}`;
        if (next !== full) this.$router.push(next).catch(() => { });
      }
      this.fetchPages(lang);
    },

    // ---------------- UI Helpers ----------------
    toggleLangDropdown() {
      this.isLangDropdownOpen = !this.isLangDropdownOpen;
    },
    handleClickOutside(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.isLangDropdownOpen = false;
      }
    },
    isActive(targetPath) {
      const cur = this.$route.path || "/";
      return cur.startsWith(targetPath.replace(/\/+$/, ""));
    },
    onScroll() {
      this.scrolled = window.scrollY > 4;
    },
  },

  mounted() {
    const langFromRoute = this.$route.params.langue || this.langue || "fr";
    if (langFromRoute !== this.langue) this.setLanguage(langFromRoute);

    this.fetchPages(this.langue);

    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("scroll", this.onScroll, { passive: true });

    this._unwatchRoute = this.$watch(
      () => this.$route.fullPath,
      () => {
        this.isMobileMenuOpen = false;
        this.isLangDropdownOpen = false;
      }
    );

    this.$watch(
      () => this.$route.params.langue,
      (newLang) => {
        if (newLang && newLang !== this.langue) {
          this.setLanguage(newLang);
          this.fetchPages(newLang);
        }
      }
    );
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.onScroll);
    if (this._unwatchRoute) this._unwatchRoute();
  }
};
</script>

<style scoped>
/* Animation Dropdown Langue */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.2s ease-out;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

/* Animation Menu Mobile */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); /* Ease-out fluide */
}

.slide-enter-from,
.slide-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>